import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Loader2, Sparkles, FileText, Languages, ArrowRight, Search } from "lucide-react";
import { toast } from "sonner";
import { Skeleton, SkeletonRow } from "@/components/Skeleton";

export default function LessonList() {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/lessons");
        setLessons(data);
      } catch { setLessons([]); }
    })();
  }, []);

  const create = async () => {
    const title = newTitle.trim();
    if (!title) { toast.error("Give it a title"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/lessons", { title });
      toast.success("Lesson created");
      navigate(`/lesson/${data.lesson_id}`);
    } catch { toast.error("Could not create lesson"); }
    finally { setCreating(false); }
  };

  const filtered = (lessons || []).filter(
    (l) => !search.trim() || l.title?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Layout title="Lessons">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-muted-foreground inline-flex items-center gap-2">
            <Sparkles className="h-3 w-3" /> Lesson workspace
          </p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
            Your teaching canvases
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground max-w-xl">
            Every lesson is a reusable workspace — notes, attached documents, generated quizzes, worksheets and presentations all in one place.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => navigate("/teach-from-anything")} data-testid="lessons-teach-cta">
            <Sparkles className="h-4 w-4 mr-1.5" /> Teach from anything
          </Button>
          <Button className="rounded-full" onClick={() => setShowNew(true)} data-testid="lessons-create-cta">
            <Plus className="h-4 w-4 mr-1.5" /> New lesson
          </Button>
        </div>
      </div>

      {/* New-lesson inline composer */}
      {showNew && (
        <div className="tb-card p-5 mb-5 tb-fade-up" data-testid="lessons-new-composer">
          <Input
            placeholder="Lesson title — e.g. Grade 8 Science · Photosynthesis"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && create()}
            className="h-11 rounded-xl"
            autoFocus
            data-testid="lessons-new-title"
          />
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="ghost" className="rounded-full" onClick={() => { setShowNew(false); setNewTitle(""); }}>Cancel</Button>
            <Button className="rounded-full" onClick={create} disabled={creating} data-testid="lessons-new-submit">
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
              Create
            </Button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mb-5 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search lessons…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-11 rounded-full" data-testid="lessons-search" />
      </div>

      {/* List */}
      {lessons === null ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="tb-card p-5"><SkeletonRow /></div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="tb-card p-10 text-center" data-testid="lessons-empty">
          <span className="mx-auto h-14 w-14 grid place-items-center rounded-full border border-dashed border-border mb-3">
            <FileText className="h-6 w-6 text-muted-foreground" />
          </span>
          <h3 className="font-display text-lg font-medium">No lessons yet</h3>
          <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
            Use <strong>Teach from anything</strong> to spin up a fully-loaded lesson from a textbook page or topic, or create a blank one to start writing.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 tb-stagger">
          {filtered.map((l) => (
            <button
              key={l.lesson_id}
              onClick={() => navigate(`/lesson/${l.lesson_id}`)}
              className="tb-card-interactive p-5 text-left"
              data-testid={`lesson-card-${l.lesson_id}`}
            >
              <p className="font-display font-medium text-base leading-snug line-clamp-2">{l.title}</p>
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                {[l.subject, l.grade && `Grade ${l.grade}`, l.language].filter(Boolean).join(" · ") || "Untitled lesson"}
              </p>
              <div className="mt-4 flex items-center gap-2 text-[11px] text-muted-foreground">
                <span className="tb-chip-lavender rounded-full px-2 py-0.5">{l.artifact_count} artifacts</span>
                {l.document_count > 0 && <span className="tb-chip-mint rounded-full px-2 py-0.5">{l.document_count} docs</span>}
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-xs text-primary">
                Open <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </button>
          ))}
        </div>
      )}
    </Layout>
  );
}
